import { supabase } from '../supabase';

export interface RateLimitResult {
  allowed: boolean;
  blocked: boolean;
  attemptCount?: number;
  delaySeconds?: number;
  reason?: string;
  retryAfter?: number;
  expiresAt?: string;
}

export async function checkRateLimit(
  identifier: string,
  identifierType: 'ip' | 'email' = 'ip'
): Promise<RateLimitResult> {
  try {
    const { data, error } = await supabase.rpc('check_rate_limit', {
      p_identifier: identifier,
      p_identifier_type: identifierType,
    });

    if (error) {
      console.error('Rate limit check error:', error);
      return { allowed: true, blocked: false };
    }

    return {
      allowed: data?.allowed ?? true,
      blocked: data?.blocked ?? false,
      attemptCount: data?.attempt_count,
      delaySeconds: data?.delay_seconds,
      reason: data?.reason,
      retryAfter: data?.retry_after,
      expiresAt: data?.expires_at,
    };
  } catch (err) {
    console.error('Rate limit check exception:', err);
    return { allowed: true, blocked: false };
  }
}

export async function resetRateLimit(
  identifier: string,
  identifierType: 'ip' | 'email' = 'ip'
): Promise<void> {
  try {
    await supabase.rpc('reset_rate_limit', {
      p_identifier: identifier,
      p_identifier_type: identifierType,
    });
  } catch (err) {
    console.error('Reset rate limit error:', err);
  }
}

export async function blockIP(
  ipAddress: string,
  reason: string,
  durationMinutes: number = 15
): Promise<void> {
  try {
    const expiresAt = new Date(Date.now() + durationMinutes * 60 * 1000).toISOString();

    await supabase.from('blocked_ips').insert({
      ip_address: ipAddress,
      reason,
      expires_at: expiresAt,
      is_permanent: false,
    });
  } catch (err) {
    console.error('Block IP error:', err);
  }
}

export async function isIPBlocked(ipAddress: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('blocked_ips')
      .select('id')
      .eq('ip_address', ipAddress)
      .or(`is_permanent.eq.true,expires_at.gt.${new Date().toISOString()}`)
      .maybeSingle();

    if (error) {
      console.error('IP block check error:', error);
      return false;
    }

    return data !== null;
  } catch (err) {
    console.error('IP block check exception:', err);
    return false;
  }
}

export function formatDelayMessage(seconds: number): string {
  if (seconds < 60) {
    return `Please wait ${seconds} seconds before trying again.`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `Please wait ${minutes} minute${minutes > 1 ? 's' : ''} before trying again.`;
}
